import { Temporal } from "@js-temporal/polyfill";

export { Temporal };

export type PromotionSummary = {
  type: "fixed" | "percentage";
  value: number;
};

export type QuoteComputationInput = {
  baseAmount: number;
  optionAmounts?: number[];
  durationMinutes: number;
  optionDurationMinutes?: number[];
  promo?: PromotionSummary | null;
  taxRate?: number;
  depositRate?: number;
};

export type QuoteComputationResult = {
  subtotal: number;
  discount: number;
  tax: number;
  tip: number;
  total: number;
  deposit: number;
  amountDueNow: number;
  durationMinutes: number;
};

export type StoredValueTenderApplication = {
  appliedAmount: number;
  remainingBookingBalance: number;
  remainingAmountDueNow: number;
};

export type SlotBooking = {
  startsAt: string;
  endsAt: string;
};

export type SlotBuildInput = {
  date: string;
  timeZone: string;
  startTime: string;
  endTime: string;
  intervalMinutes: number;
  durationMinutes: number;
  bufferBeforeMinutes?: number;
  bufferAfterMinutes?: number;
  leadMinutes?: number;
  capacity: number;
  maxSimultaneousBookings?: number;
  existingBookings?: SlotBooking[];
  staffCapacityBySlot?: (slotStart: string, slotEnd: string) => number;
  now?: Temporal.Instant;
};

export type AvailabilitySlot = {
  startsAt: string;
  endsAt: string;
  startsAtLocal: string;
  endsAtLocal: string;
  remainingCapacity: number;
};

export type DepositChoice = "none" | "deposit" | "full";

const weekdayLabels = [
  "monday",
  "tuesday",
  "wednesday",
  "thursday",
  "friday",
  "saturday",
  "sunday"
] as const;

let defaultCurrencyCode = "USD";
let defaultCurrencyLocale = "en-US";

export function safeJsonParse<T>(value: string | null | undefined, fallback: T): T {
  if (!value) {
    return fallback;
  }

  try {
    return JSON.parse(value) as T;
  } catch {
    return fallback;
  }
}

export function configureMoneyFormatting(input: { currency?: string | null; locale?: string | null }) {
  defaultCurrencyCode = String(input.currency ?? "").trim().toUpperCase() || "USD";
  defaultCurrencyLocale = String(input.locale ?? "").trim() || "en-US";
}

export function formatCurrency(amountInCents: number, currency = defaultCurrencyCode, locale = defaultCurrencyLocale): string {
  return new Intl.NumberFormat(locale, {
    style: "currency",
    currency
  }).format(amountInCents / 100);
}

function resolveTemplateVariable(variables: Record<string, unknown>, key: string): unknown {
  if (key in variables) {
    return variables[key];
  }

  return key.split(".").reduce<unknown>((current, part) => {
    if (current && typeof current === "object" && part in (current as Record<string, unknown>)) {
      return (current as Record<string, unknown>)[part];
    }
    return undefined;
  }, variables);
}

export function renderTemplate(template: string, variables: Record<string, unknown>): string {
  return template.replace(/\{\{\s*([a-zA-Z0-9_.-]+)\s*\}\}/g, (_match, key) => {
    const value = resolveTemplateVariable(variables, key);
    return value == null ? "" : String(value);
  });
}

export function buildSmsReminderOptInDisclosure(_businessName?: string): string {
  return "Send me text message reminders. Reply STOP to opt out.";
}

export function ensureSmsOptOutLanguage(message: string, optOutLine = "Reply STOP to opt out of reminder texts."): string {
  const normalizedMessage = String(message ?? "").trim();
  if (!normalizedMessage) {
    return optOutLine;
  }

  if (/reply\s+stop/i.test(normalizedMessage)) {
    return normalizedMessage;
  }

  return `${normalizedMessage} ${optOutLine}`.trim();
}

export function normalizePhoneForAudit(value: string): string {
  const digits = String(value ?? "").replace(/\D/g, "");
  if (digits.length === 11 && digits.startsWith("1")) {
    return `+${digits}`;
  }
  if (digits.length === 10) {
    return `+1${digits}`;
  }
  return String(value ?? "").trim();
}

export function resolveCheckoutDepositTerms(input: {
  paymentPreferenceEnabled: boolean;
  configuredDepositRate?: number | null;
  requestedDepositChoice: DepositChoice;
}) {
  const configuredDepositRateRaw = Number(input.configuredDepositRate ?? 0.2);
  const configuredDepositRate = Number.isFinite(configuredDepositRateRaw)
    ? Math.min(1, Math.max(0, configuredDepositRateRaw))
    : 0.2;

  const depositChoice = input.paymentPreferenceEnabled
    ? input.requestedDepositChoice
    : configuredDepositRate >= 1
      ? "full"
      : "deposit";

  const depositRate = input.paymentPreferenceEnabled
    ? depositChoice === "full"
      ? 1
      : depositChoice === "none"
        ? 0
        : configuredDepositRate > 0
          ? configuredDepositRate
          : 0.2
    : configuredDepositRate > 0
      ? configuredDepositRate
      : 0.2;

  return {
    depositChoice,
    depositRate
  };
}

function normalizeBaseUrl(value: string | null | undefined) {
  const trimmed = String(value ?? "").trim();
  if (!trimmed) {
    return "";
  }

  try {
    const url = new URL(trimmed);
    if (url.protocol !== "http:" && url.protocol !== "https:") {
      return "";
    }
    url.search = "";
    url.hash = "";
    url.pathname = url.pathname.replace(/\/+$/, "");
    return url.toString().replace(/\/$/, "");
  } catch {
    return "";
  }
}

export function resolveCustomerFacingBaseUrl(
  configuredBaseUrl: string | null | undefined,
  fallbackBaseUrl: string
) {
  return normalizeBaseUrl(configuredBaseUrl) || normalizeBaseUrl(fallbackBaseUrl) || String(fallbackBaseUrl ?? "").trim();
}

export function buildCustomerFacingUrl(baseUrl: string, pathname: string) {
  const url = new URL(resolveCustomerFacingBaseUrl(baseUrl, baseUrl));
  const basePath = url.pathname === "/" ? "" : url.pathname.replace(/\/+$/, "");
  const normalizedPath = `/${String(pathname ?? "").replace(/^\/+/, "")}`;
  url.pathname = `${basePath}${normalizedPath}`;
  url.search = "";
  url.hash = "";
  return url;
}

export function combineDateAndTime(date: string, time: string, timeZone: string): Temporal.ZonedDateTime {
  const [year, month, day] = date.split("-").map(Number);
  const [hour, minute] = time.split(":").map(Number);

  return Temporal.ZonedDateTime.from({
    timeZone,
    year,
    month,
    day,
    hour,
    minute
  });
}

export function getLocalDateForInstant(instant: string, timeZone: string): string {
  return Temporal.Instant.from(instant).toZonedDateTimeISO(timeZone).toPlainDate().toString();
}

export function getUtcBoundsForLocalDate(date: string, timeZone: string): {
  startInclusive: string;
  endExclusive: string;
} {
  const dayStart = combineDateAndTime(date, "00:00", timeZone);
  return {
    startInclusive: dayStart.toInstant().toString(),
    endExclusive: dayStart.add({ days: 1 }).toInstant().toString()
  };
}

export function resolveCustomerPortalChangeWindow(input: {
  startsAt: string;
  timeZone?: string | null;
  cancellationPolicy?: string | null;
  cancellationPolicyMode?: "day_before_5pm" | "custom" | null;
  cancellationPresetDaysBefore?: number | null;
  cancellationPresetTime?: string | null;
  cancellationCutoffValue?: number | null;
  cancellationCutoffUnit?: "hours" | "days" | null;
  now?: string | Date | null;
}) {
  const timeZone = String(input.timeZone ?? "").trim() || "America/Chicago";
  const cancellationPolicy = String(input.cancellationPolicy ?? "").trim();

  let startsAtInstant: Temporal.Instant | null = null;
  try {
    startsAtInstant = Temporal.Instant.from(String(input.startsAt ?? ""));
  } catch {
    startsAtInstant = null;
  }

  let nowInstant = Temporal.Now.instant();
  if (input.now instanceof Date && Number.isFinite(input.now.getTime())) {
    nowInstant = Temporal.Instant.from(input.now.toISOString());
  } else if (typeof input.now === "string" && input.now.trim()) {
    const parsed = new Date(input.now);
    if (Number.isFinite(parsed.getTime())) {
      nowInstant = Temporal.Instant.from(parsed.toISOString());
    }
  }

  let cutoffInstant: Temporal.Instant | null = null;
  if (startsAtInstant) {
    if (input.cancellationPolicyMode === "custom") {
      const cutoffValue = Math.max(1, Number(input.cancellationCutoffValue ?? 47) || 47);
      cutoffInstant =
        input.cancellationCutoffUnit === "days"
          ? startsAtInstant.toZonedDateTimeISO(timeZone).subtract({ days: cutoffValue }).toInstant()
          : startsAtInstant.subtract({ hours: cutoffValue });
    } else {
      const bookingLocal = startsAtInstant.toZonedDateTimeISO(timeZone);
      const cutoffDate = bookingLocal.toPlainDate().subtract({
        days: Math.max(1, Number(input.cancellationPresetDaysBefore ?? 1) || 1)
      });
      const [hourRaw, minuteRaw] = String(input.cancellationPresetTime ?? "17:00").split(":");
      const hour = Number.isFinite(Number(hourRaw)) ? Number(hourRaw) : 17;
      const minute = Number.isFinite(Number(minuteRaw)) ? Number(minuteRaw) : 0;
      cutoffInstant = Temporal.ZonedDateTime.from({
        timeZone,
        year: cutoffDate.year,
        month: cutoffDate.month,
        day: cutoffDate.day,
        hour,
        minute
      }).toInstant();
    }
  }

  return {
    cancellationPolicy,
    changeCutoffAt: cutoffInstant?.toString() ?? "",
    changesLocked: cutoffInstant ? Temporal.Instant.compare(nowInstant, cutoffInstant) >= 0 : false
  };
}

const customerPortalRebookAdminDetailDenyList = new Set([
  "amount_paid_by_customer",
  "amount_owed_by_customer",
  "service_total",
  "extras_total",
  "final_amount",
  "tip_type",
  "tip",
  "payment_method",
  "payment_authorized",
  "frequency_discount_type",
  "frequency_discount",
  "discount_code",
  "discount_from_code",
  "discount_from_frequency",
  "gift_card_number",
  "giftcard_amount_used",
  "invoice_number",
  "quote_received",
  "quote_labor_hours",
  "quote_amount",
  "booking_id",
  "customer_id",
  "date_created",
  "created_by",
  "next_booking_date",
  "date_cancelled",
  "cancelled_by",
  "cancellation_reason",
  "cancellation_fee",
  "price_adjustment",
  "is_price_adjustment_after_tax",
  "price_adjustment_comment",
  "team_earnings_amount",
  "team_earnings_summary",
  "preferred_staff",
  "blocked_staff",
  "english_speaking_staff",
  "team_has_key",
  "teams_assigned_without_ids",
  "teams_assigned",
  "services",
  "extras",
  "extra_recurring_summary",
  "remodeling_summary",
  "entry_information",
  "arrival_flexibility"
]);

export function sanitizeCustomerPortalRebookAdminDetails(details: Record<string, string> | null | undefined) {
  const source = details ?? {};
  return Object.fromEntries(
    Object.entries(source).filter(([key, value]) => !customerPortalRebookAdminDetailDenyList.has(key) && String(value ?? "").trim() !== "")
  );
}

function normalizeBookingExtraSummaryLines(value: string | null | undefined) {
  return Array.from(
    new Set(
      String(value ?? "")
        .split(/[|\n]/)
        .map((entry) => entry.trim())
        .filter(Boolean)
    )
  );
}

export function summarizeBookingExtras(details: Record<string, string> | null | undefined) {
  const source = details ?? {};
  const lines = Array.from(
    new Set([
      ...normalizeBookingExtraSummaryLines(source.extras),
      ...normalizeBookingExtraSummaryLines(source.extra_recurring_summary)
    ])
  );

  return {
    lines,
    summary: lines.join(", ")
  };
}

export function resolveBookingChangeNotification(input: {
  previousStatus?: string | null;
  nextStatus?: string | null;
  previousStartsAt?: string | null;
  nextStartsAt?: string | null;
  previousAdminDetails?: Record<string, string> | null;
  nextAdminDetails?: Record<string, string> | null;
}) {
  const previousExtras = summarizeBookingExtras(input.previousAdminDetails);
  const currentExtras = summarizeBookingExtras(input.nextAdminDetails);
  const previousExtrasSet = new Set(previousExtras.lines.map((entry) => entry.toLowerCase()));
  const currentExtrasSet = new Set(currentExtras.lines.map((entry) => entry.toLowerCase()));
  const addedExtras = currentExtras.lines.filter((entry) => !previousExtrasSet.has(entry.toLowerCase()));
  const removedExtras = previousExtras.lines.filter((entry) => !currentExtrasSet.has(entry.toLowerCase()));
  const extrasChanged =
    addedExtras.length > 0 ||
    removedExtras.length > 0 ||
    previousExtrasSet.size !== currentExtrasSet.size;
  const cancellationChanged =
    String(input.previousStatus ?? "").trim().toLowerCase() !== "cancelled" &&
    String(input.nextStatus ?? "").trim().toLowerCase() === "cancelled";
  const scheduleChanged =
    String(input.previousStartsAt ?? "").trim() !== "" &&
    String(input.nextStartsAt ?? "").trim() !== "" &&
    String(input.previousStartsAt ?? "").trim() !== String(input.nextStartsAt ?? "").trim();

  return {
    eventKey: cancellationChanged
      ? ("booking_cancellation" as const)
      : scheduleChanged
        ? ("booking_rescheduled" as const)
        : extrasChanged
          ? ("booking_extras_changed" as const)
          : null,
    extrasChanged,
    includesExtrasChanges: scheduleChanged && extrasChanged,
    previousExtrasSummary: previousExtras.summary,
    currentExtrasSummary: currentExtras.summary,
    addedExtras,
    removedExtras
  };
}

export function weekdayForDate(date: string, timeZone: string): (typeof weekdayLabels)[number] {
  return weekdayLabels[combineDateAndTime(date, "00:00", timeZone).dayOfWeek - 1];
}

export function calculateQuote(input: QuoteComputationInput): QuoteComputationResult {
  const subtotal = input.baseAmount + (input.optionAmounts ?? []).reduce((sum, value) => sum + value, 0);
  const rawDiscount =
    input.promo == null
      ? 0
      : input.promo.type === "fixed"
        ? input.promo.value
        : Math.round(subtotal * (input.promo.value / 100));
  const discount = Math.min(subtotal, rawDiscount);
  const taxableAmount = Math.max(0, subtotal - discount);
  const tax = Math.round(taxableAmount * (input.taxRate ?? 0));
  const total = taxableAmount + tax;
  const deposit = Math.round(total * (input.depositRate ?? 0));
  const durationMinutes =
    input.durationMinutes + (input.optionDurationMinutes ?? []).reduce((sum, value) => sum + value, 0);

  return {
    subtotal,
    discount,
    tax,
    tip: 0,
    total,
    deposit,
    amountDueNow: deposit,
    durationMinutes
  };
}

export function applyStoredValueTender(input: {
  bookingTotal: number;
  amountDueNow: number;
  availableAmount: number;
}): StoredValueTenderApplication {
  const bookingTotal = Math.max(0, Number(input.bookingTotal ?? 0));
  const amountDueNow = Math.max(0, Number(input.amountDueNow ?? 0));
  const availableAmount = Math.max(0, Number(input.availableAmount ?? 0));
  const appliedAmount = Math.min(bookingTotal, availableAmount);

  return {
    appliedAmount,
    remainingBookingBalance: Math.max(0, bookingTotal - appliedAmount),
    remainingAmountDueNow: Math.max(0, amountDueNow - appliedAmount)
  };
}

export function buildAvailabilitySlots(input: SlotBuildInput): AvailabilitySlot[] {
  const results: AvailabilitySlot[] = [];
  const bufferBeforeMinutes = input.bufferBeforeMinutes ?? 0;
  const bufferAfterMinutes = input.bufferAfterMinutes ?? 0;
  const slotRuntimeMinutes = input.durationMinutes + bufferBeforeMinutes + bufferAfterMinutes;
  const start = combineDateAndTime(input.date, input.startTime, input.timeZone);
  const end = combineDateAndTime(input.date, input.endTime, input.timeZone);
  const minimumInstant = (input.now ?? Temporal.Now.instant()).add({ minutes: input.leadMinutes ?? 0 });
  const maxCapacity = Math.min(input.capacity, input.maxSimultaneousBookings ?? input.capacity);
  const existing = (input.existingBookings ?? []).map((booking) => ({
    startsAt: Temporal.Instant.from(booking.startsAt),
    endsAt: Temporal.Instant.from(booking.endsAt)
  }));

  for (
    let cursor = start;
    Temporal.ZonedDateTime.compare(cursor.add({ minutes: slotRuntimeMinutes }), end) <= 0;
    cursor = cursor.add({ minutes: input.intervalMinutes })
  ) {
    const slotStart = cursor.add({ minutes: bufferBeforeMinutes }).toInstant();
    const slotEnd = slotStart.add({ minutes: input.durationMinutes });
    if (Temporal.Instant.compare(slotStart, minimumInstant) < 0) {
      continue;
    }

    const overlappingCount = existing.filter(
      (booking) =>
        Temporal.Instant.compare(slotStart, booking.endsAt) < 0 &&
        Temporal.Instant.compare(slotEnd, booking.startsAt) > 0
    ).length;

    const staffCapacity =
      input.staffCapacityBySlot?.(slotStart.toString(), slotEnd.toString()) ?? Number.POSITIVE_INFINITY;
    const remainingCapacity = Math.max(0, Math.min(maxCapacity, staffCapacity) - overlappingCount);

    if (remainingCapacity > 0) {
      results.push({
        startsAt: slotStart.toString(),
        endsAt: slotEnd.toString(),
        startsAtLocal: cursor.toPlainDateTime().toString(),
        endsAtLocal: cursor.add({ minutes: input.durationMinutes }).toPlainDateTime().toString(),
        remainingCapacity
      });
    }
  }

  return results;
}

export function createSlug(value: string): string {
  return value
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");
}
